/**
 * Menu lateral do DESKTOP (≥1024px) — WEB-only.
 *
 * No celular/tablet a navegação é a `BottomBar` (barra inferior + sheet do Menu). No
 * desktop o shell mostra este menu fixo à esquerda no lugar dela, com os MESMOS
 * destinos, ícones, regras de visibilidade (permissões do posto, contrato, conta
 * inativa, em turno), badge de documentos e ações (registrar ponto com os mesmos
 * bloqueios, Acessar Administrativo, Mudar Perfil, Sair com o bloqueio por pendência
 * de sincronização). Tudo vem de `./itensMenu`, a mesma fonte da BottomBar.
 */
import { setTourTarget } from "@/components/onboarding/tourTargets";
import { useAuth } from "@/contexts/AuthContext";
import { MaterialIcons } from "@/ui/icons";
import { cx } from "@/ui/cx";
import { useGlobalSearchParams, usePathname } from "@/navigation";
import EmpresaSwitcher, { type EmpresaSwitcherRef } from "@/components/pageFragment/EmpresaSwitcher";
import { useEmpresaLogo } from "@/components/pageFragment/empresaLogo";
import { Image } from "@/ui/Image";
import { useRef, useState } from "react";
import { ModaisSaida } from "./BottomBar";
import {
  ITENS_CONTA,
  PENDENCIAS_VAZIAS,
  abrirSuporteChat,
  acessarAdministrativo,
  caminhoDaAba,
  ehDesktop,
  executarSaida,
  mudarPerfil,
  navegarParaAba,
  pendenciasQueBloqueiam,
  registrarPonto,
  useRegrasMenu,
  type PendenciasSync,
} from "./itensMenu";
import s from "./MenuLateral.module.css";
import { T, TM } from "@/utils/i18n";

/** Registra o alvo do tour só quando este menu é o visível (desktop). */
const alvoTour = (key: string) => (node: HTMLElement | null) => {
  if (!ehDesktop()) return;
  setTourTarget(key, node as any);
};

/** Iniciais do nome para o avatar do rodapé. */
const iniciais = (nome: string) => {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  const primeira = partes[0][0] ?? "";
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] ?? "" : "";
  return `${primeira}${ultima}`.toUpperCase();
};

export default function MenuLateral() {
  const { signOut } = useAuth();
  const rotaAtual = usePathname();
  const paramsRota = useGlobalSearchParams() as Record<string, unknown>;
  const {
    userData,
    items,
    menuGrupos,
    inativo,
    temAcessoAdm,
    portalHash,
    permitePonto,
  } = useRegrasMenu();

  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [bloqueioSaidaOpen, setBloqueioSaidaOpen] = useState(false);
  const [pendenciasSync, setPendenciasSync] =
    useState<PendenciasSync>(PENDENCIAS_VAZIAS);

  // Mesma regra da BottomBar: some enquanto a Ronda está lendo QR / tirando foto.
  const hidden =
    rotaAtual === caminhoDaAba("ronda") &&
    String(paramsRota?.scanning ?? "") === "true";

  const ativo = (route: string) => rotaAtual === caminhoDaAba(route);
  const suporteAtivo = (rotaAtual ?? "").startsWith("/suporte-chat");

  // Mesmo fluxo do "Sair do Aplicativo" do menu: bloqueia se houver pendência de
  // sincronização (ou se não deu para conferir); senão pede confirmação.
  const abrirSair = async () => {
    const bloqueio = await pendenciasQueBloqueiam();
    if (bloqueio) {
      setPendenciasSync(bloqueio);
      setBloqueioSaidaOpen(true);
      return;
    }
    setLogoutModalOpen(true);
  };

  /**
   * Abre o bloqueio DEPOIS que o modal de confirmação terminou de fechar (mesmo
   * intervalo da BottomBar).
   */
  const bloquearDepoisDaConfirmacao = (pend: PendenciasSync) => {
    setPendenciasSync(pend);
    setTimeout(() => setBloqueioSaidaOpen(true), 450);
  };

  const confirmLogout = async () => {
    setLogoutModalOpen(false);
    await executarSaida(signOut, bloquearDepoisDaConfirmacao);
  };

  // Seletor de empresa (desktop): mesmo seletor do cabeçalho do app, aqui no topo do menu.
  // ⚠️ Hooks ANTES do `return null` abaixo: com eles depois, entrar na leitura de QR da
  // Ronda (hidden) rodava menos hooks que o render anterior → React #300 ("Algo deu errado").
  const empresaLogo = useEmpresaLogo();
  const switcherRef = useRef<EmpresaSwitcherRef>(null);

  if (hidden) return null;

  // Os itens da barra (Home · Arquivos · Suporte) abrem a navegação; "Menu" não entra
  // como item: o menu inteiro já está aberto aqui. "Arquivos Recebidos" dos grupos leva
  // ao MESMO destino da aba Arquivos (que já mostra o badge) — não repete.
  const itensNavegacao = items.filter((it) => it.key !== "menu");
  const rotasNavegacao = new Set(
    itensNavegacao.filter((it) => it.key !== "suporte").map((it) => it.key),
  );
  const grupos = menuGrupos
    .map((g) => ({
      ...g,
      itens: g.itens.filter((it) => !rotasNavegacao.has(it.route as any)),
    }))
    .filter((g) => g.itens.length > 0);

  const nome = String(userData?.nome ?? "").trim();
  const empresa = String(userData?.posto ?? "").trim();
  const ud = userData as any;
  const nomeEmpresa = String(
    ud?.nome_fantasia ?? ud?.empresa?.nome_fantasia ?? ud?.nome_empresa ?? ud?.razao_social ?? ud?.empresa?.razao_social ?? empresa ?? "",
  ).trim();
  const pontoDesativado = inativo || !permitePonto;

  return (
    <nav className={s.raiz} aria-label={T("Menu principal")}>
      <div className={s.topo}>
        <button
          type="button"
          className={s.empresaBotao}
          onClick={() => switcherRef.current?.present()}
          aria-label={T("Trocar de empresa")}
          title={T("Trocar de empresa")}
        >
          <div className={s.empresaLogoBox}>
            <Image source={empresaLogo} className={s.empresaLogo} resizeMode="contain" alt="" />
          </div>
          <div className={s.empresaTextos}>
            <span className={s.empresaRotulo}>{T("Empresa")}</span>
            <span className={s.empresaNome}>{nomeEmpresa || T("Selecionar empresa")}</span>
          </div>
          <MaterialIcons name="unfold-more" size={20} color="#cfe3ea" />
        </button>
        <EmpresaSwitcher ref={switcherRef} />
      </div>

      <div className={cx(s.rolagem, "sem-scrollbar")}>
        {/* Botão central da barra (câmera) — mesmo destino e mesmos bloqueios. */}
        <button
          type="button"
          ref={alvoTour("bottombar:camera")}
          onClick={() => registrarPonto(inativo, permitePonto, rotaAtual)}
          className={cx(
            s.registrarPonto,
            pontoDesativado && s.registrarPontoDesativado,
            ativo("camera") && s.registrarPontoAtivo,
          )}
        >
          <MaterialIcons name="photo-camera" size={22} color="#294956" />
          <span className={s.registrarPontoTexto}>{T("Registrar ponto")}</span>
        </button>

        {temAcessoAdm && !inativo && (
          <button
            type="button"
            onClick={() => void acessarAdministrativo(portalHash)}
            className={s.adm}
          >
            <div className={s.admPastilha}>
              <MaterialIcons name="admin-panel-settings" size={22} color="#fff" />
            </div>
            <div className={s.admTextos}>
              <span className={s.admTitulo}>{T("Acessar Administrativo")}</span>
              <span className={s.admSub}>
                {T("Entrar no painel administrativo da empresa")}
              </span>
            </div>
            <MaterialIcons name="arrow-forward" size={18} color="#fff" />
          </button>
        )}

        <div className={s.grupo}>
          {itensNavegacao.map((item) => {
            const estaAtivo =
              item.key === "suporte" ? suporteAtivo : ativo(item.key);
            return (
              <button
                key={item.key}
                type="button"
                ref={alvoTour(`bottombar:${item.key}`)}
                aria-current={estaAtivo ? "page" : undefined}
                onClick={() => {
                  if (item.key === "suporte") {
                    abrirSuporteChat();
                  } else {
                    navegarParaAba(item.key, rotaAtual);
                  }
                }}
                className={cx(s.item, estaAtivo && s.itemAtivo)}
              >
                <div className={s.icone}>
                  <MaterialIcons name={item.icon} size={22} color="#fff" />
                  {item.badge ? <div className={s.badge} /> : null}
                </div>
                <span className={s.rotulo}>{item.label}</span>
              </button>
            );
          })}
        </div>

        {grupos.map((grupo, gi) => (
          <div
            key={grupo.id}
            className={s.grupo}
            // O alvo "menu" do tour aponta para o primeiro grupo do menu.
            ref={gi === 0 ? alvoTour("bottombar:menu") : undefined}
          >
            <span className={s.tituloGrupo}>{grupo.titulo}</span>
            {grupo.itens.map((it) => {
              const estaAtivo = ativo(it.route);
              return (
                <button
                  key={it.route}
                  type="button"
                  aria-current={estaAtivo ? "page" : undefined}
                  onClick={() => navegarParaAba(it.route, rotaAtual)}
                  className={cx(s.item, estaAtivo && s.itemAtivo)}
                  title={it.sub}
                >
                  <div className={s.icone}>
                    <MaterialIcons name={it.icon as any} size={22} color="#fff" />
                  </div>
                  <span className={s.rotulo}>{it.label}</span>
                </button>
              );
            })}
          </div>
        ))}

        <div
          className={s.grupo}
          ref={grupos.length === 0 ? alvoTour("bottombar:menu") : undefined}
        >
          <span className={s.tituloGrupo}>{T("Conta")}</span>
          {ITENS_CONTA.map((it) => {
            const estaAtivo = it.route ? ativo(it.route) : false;
            return (
              <button
                key={it.label}
                type="button"
                aria-current={estaAtivo ? "page" : undefined}
                onClick={() => {
                  if (it.acao === "mudarPerfil") mudarPerfil();
                  else if (it.route) navegarParaAba(it.route, rotaAtual);
                }}
                className={cx(s.item, estaAtivo && s.itemAtivo)}
              >
                <div className={s.icone}>
                  <MaterialIcons name={it.icon as any} size={22} color="#fff" />
                </div>
                {/* ITENS_CONTA é constante do arquivo (TC): traduz aqui. */}
                <span className={s.rotulo}>{TM(it.label)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={s.rodape}>
        <div className={s.usuario}>
          <div className={s.avatar}>
            <span className={s.avatarTexto}>{iniciais(nome)}</span>
          </div>
          <div className={s.usuarioTextos}>
            <span className={s.usuarioNome}>{nome || T("Colaborador")}</span>
            {empresa ? <span className={s.usuarioEmpresa}>{empresa}</span> : null}
          </div>
        </div>
        <button type="button" onClick={abrirSair} className={s.sair}>
          <MaterialIcons name="logout" size={18} color="#ffb4b5" />
          <span className={s.sairTexto}>{T("Sair do Aplicativo")}</span>
        </button>
      </div>

      <ModaisSaida
        logoutModalOpen={logoutModalOpen}
        onCloseLogout={() => setLogoutModalOpen(false)}
        onConfirmLogout={confirmLogout}
        bloqueioSaidaOpen={bloqueioSaidaOpen}
        onCloseBloqueio={() => setBloqueioSaidaOpen(false)}
        pendenciasSync={pendenciasSync}
      />
    </nav>
  );
}
